import { assertNever } from "@redotech/util/type";
import * as z from "zod/v4";
import { disputeSource } from "./dispute-definition";
import { disputeEvidenceSchema_ } from "./dispute-evidence-definition";

export const disputeViewStatus = [
  "ACCEPTED",
  "CHARGE_REFUNDED",
  "LOST",
  "NEEDS_RESPONSE",
  "UNDER_REVIEW",
  "WON",
] as const;

export type DisputeViewStatus = (typeof disputeViewStatus)[number];

export const disputeViewType = ["CHARGEBACK", "INQUIRY"] as const;

export type DisputeViewType = (typeof disputeViewType)[number];

const DisputeItemSchema_ = z.object({
  id: z.string(),
  source: z.enum(disputeSource),
  amount: z.number(),
  currency: z.string(),
  status: z.enum(disputeViewStatus),
  type: z.enum(disputeViewType),
  reason: z.string(),
  networkReasonCode: z.nullable(z.string()),
  initiatedAt: z.string(),
  evidenceDueBy: z.nullable(z.string()),
  evidenceSentOn: z.nullable(z.string()),
  finalizedOn: z.nullable(z.string()),
  orderName: z.nullable(z.string()),
});

export const DisputeItemSchema: z.ZodType<DisputeItem> = DisputeItemSchema_;
export interface DisputeItem extends z.infer<typeof DisputeItemSchema_> {}

// Evidence file upload

export const disputeEvidenceFileType = [
  "CANCELLATION_POLICY_FILE",
  "CUSTOMER_COMMUNICATION_FILE",
  "REFUND_POLICY_FILE",
  "SERVICE_DOCUMENTATION_FILE",
  "SHIPPING_DOCUMENTATION_FILE",
  "UNCATEGORIZED_FILE",
] as const;

export type DisputeEvidenceFileType = (typeof disputeEvidenceFileType)[number];

const DisputeFileUploadSchema_ = z.object({
  id: z.string(),
  originalFileName: z.nullable(z.string()),
  fileSize: z.number(),
  fileType: z.string(),
  url: z.string(),
  disputeEvidenceType: z.nullable(z.enum(disputeEvidenceFileType)),
});

export const DisputeFileUploadSchema: z.ZodType<DisputeFileUpload> =
  DisputeFileUploadSchema_;
export interface DisputeFileUpload
  extends z.infer<typeof DisputeFileUploadSchema_> {}

// Fulfillment info

const DisputeFulfillmentSchema_ = z.object({
  id: z.string(),
  shippingCarrier: z.nullable(z.string()),
  shippingTrackingNumber: z.nullable(z.string()),
  shippingDate: z.nullable(z.string()),
});

export const DisputeFulfillmentSchema: z.ZodType<DisputeFulfillment> =
  DisputeFulfillmentSchema_;
export interface DisputeFulfillment
  extends z.infer<typeof DisputeFulfillmentSchema_> {}

// Detail (extends list item with evidence)

const DisputeDetailSchema_ = DisputeItemSchema_.extend({
  networkReasonCode: z.nullable(z.string()),
  evidence: z.nullable(disputeEvidenceSchema_),
});

export const DisputeDetailSchema: z.ZodType<DisputeDetail> =
  DisputeDetailSchema_;
export interface DisputeDetail extends z.infer<typeof DisputeDetailSchema_> {}

// Evidence categories for the UI

export const disputeEvidenceCategory = [
  "CUSTOMER_COMMUNICATION",
  "SHIPPING_DOCUMENTATION",
  "UNCATEGORIZED",
] as const;

export type DisputeEvidenceCategory = (typeof disputeEvidenceCategory)[number];

// Timeline steps

export const disputeTimelineStep = [
  "DISPUTE_CREATED",
  "SUBMIT_EVIDENCE",
  "BANK_REVIEW",
  "DISPUTE_SETTLED",
] as const;

export type DisputeTimelineStep = (typeof disputeTimelineStep)[number];

export function getActiveTimelineStep(
  status: DisputeViewStatus,
): DisputeTimelineStep {
  switch (status) {
    case "NEEDS_RESPONSE":
      return "SUBMIT_EVIDENCE";
    case "UNDER_REVIEW":
      return "BANK_REVIEW";
    case "WON":
    case "LOST":
    case "ACCEPTED":
    case "CHARGE_REFUNDED":
      return "DISPUTE_SETTLED";
    default:
      assertNever(status);
  }
}
